/**
 * Shared Commission Estimator body (inputs + live itemized estimate). Pure — takes the rate set as a
 * prop and does no fetching, so it's reused by both the authenticated page (CommissionEstimator) and the
 * standalone public page (CommissionEstimatorPublic). Math lives in utils/commissionEstimator (unit-tested).
 */
import { useMemo, useState } from "react";
import {
  estimateCommission,
  EMPTY_ESTIMATOR_INPUTS,
  type CommissionEstimatorInputs,
  type CommissionEstimatorRate,
  type EstimateLineItem,
} from "../../utils/commissionEstimator";

const usd = (n: number): string =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

type InputKey = keyof CommissionEstimatorInputs;

const EVENT_FIELDS: { key: InputKey; label: string }[] = [
  { key: "classesWithSale", label: "Classes worked (with a sale)" },
  { key: "gunShowTablesWithSale", label: "Gun shows / tables (with a sale)" },
  { key: "seminarsWithSale", label: "Seminars (with a sale)" },
  {
    key: "oneTimeEvents",
    label: "One-time events (Base Pay, 0 Sales for the Day)",
  },
];

const SALE_FIELDS: { key: InputKey; label: string }[] = [
  { key: "verifiedSales", label: "Verified sales (new members)" },
  { key: "passiveSales", label: "Passive sales (assigned accounts)" },
  { key: "leadScans", label: "Lead scans (assigned accounts)" },
  { key: "upstreamSales", label: "Upstream sales (assigned accounts)" },
];

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-sm text-gray-700">{label}</span>
      <input
        type="number"
        min={0}
        step={1}
        value={value === 0 ? "" : value}
        placeholder="0"
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          onChange(Number.isFinite(n) && n > 0 ? n : 0);
        }}
        className="w-24 rounded border border-gray-300 px-2 py-1 text-right text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
      />
    </label>
  );
}

function LineRow({ item }: { item: EstimateLineItem }) {
  if (item.quantity === 0) {
    return null;
  }
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-gray-600">
        {item.label}
        <span className="ml-1 text-xs text-gray-400">
          ({item.quantity} × {usd(item.unitAmount)})
        </span>
      </span>
      <span className="font-medium text-gray-900">{usd(item.subtotal)}</span>
    </div>
  );
}

export default function CommissionEstimatorForm({
  rates,
}: {
  rates: CommissionEstimatorRate[];
}) {
  const [inputs, setInputs] = useState<CommissionEstimatorInputs>({
    ...EMPTY_ESTIMATOR_INPUTS,
  });

  const estimate = useMemo(
    () => estimateCommission(inputs, rates),
    [inputs, rates],
  );

  const setField = (key: InputKey, n: number) =>
    setInputs((prev) => ({ ...prev, [key]: n }));

  const anyInput = Object.values(inputs).some((v) => v > 0);

  return (
    <>
      <div className="mb-1 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Enter your expected activity for a{" "}
          <span className="font-semibold text-gray-700">typical month</span> to
          see what it could add up to per month and per year. This is an
          estimate (it counts Day 1 + Day 31 as the potential maximum; Day 31
          pay requires the member to remain active, and the yearly figure
          assumes this activity level every month).
        </p>
        <button
          type="button"
          onClick={() => setInputs({ ...EMPTY_ESTIMATOR_INPUTS })}
          className="ml-3 shrink-0 text-sm text-gray-500 underline hover:text-gray-700"
        >
          Reset
        </button>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Inputs */}
        <div className="space-y-6">
          <div className="rounded-lg border border-gray-200 p-4">
            <h2 className="mb-2 text-sm font-semibold text-gray-900">
              Events worked{" "}
              <span className="font-normal text-gray-400">(in a month)</span>
            </h2>
            <div className="divide-y divide-gray-100">
              {EVENT_FIELDS.map((f) => (
                <NumberField
                  key={f.key}
                  label={f.label}
                  value={inputs[f.key]}
                  onChange={(n) => setField(f.key, n)}
                />
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 p-4">
            <h2 className="mb-2 text-sm font-semibold text-gray-900">
              Sales &amp; conversions{" "}
              <span className="font-normal text-gray-400">(in a month)</span>
            </h2>
            <div className="divide-y divide-gray-100">
              {SALE_FIELDS.map((f) => (
                <NumberField
                  key={f.key}
                  label={f.label}
                  value={inputs[f.key]}
                  onChange={(n) => setField(f.key, n)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Estimate */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h2 className="mb-3 text-sm font-semibold text-gray-900">
            Estimated commission
          </h2>

          {!anyInput ? (
            <p className="text-sm text-gray-500">
              Enter some activity on the left to see an estimate.
            </p>
          ) : (
            <>
              <div className="mb-2">
                <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">
                  Event pay
                </div>
                {estimate.eventPay.map((l) => (
                  <LineRow key={l.key} item={l} />
                ))}
                <div className="mt-1 flex justify-between border-t border-gray-200 pt-1 text-sm">
                  <span className="text-gray-500">Event pay subtotal</span>
                  <span className="font-medium">
                    {usd(estimate.eventPayTotal)}
                  </span>
                </div>
              </div>

              <div className="mb-3">
                <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">
                  Conversion commission
                </div>
                {estimate.conversion.map((l) => (
                  <LineRow key={l.key} item={l} />
                ))}
                <div className="mt-1 flex justify-between border-t border-gray-200 pt-1 text-sm">
                  <span className="text-gray-500">Conversion subtotal</span>
                  <span className="font-medium">
                    {usd(estimate.conversionTotal)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 border-t-2 border-gray-300 pt-3">
                <div className="rounded-lg border border-gray-200 bg-white p-3 text-center">
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-400">
                    Per month
                  </div>
                  <div className="mt-1 text-xl font-bold text-gray-900">
                    {usd(estimate.total)}
                  </div>
                </div>
                <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 text-center">
                  <div className="text-xs font-medium uppercase tracking-wide text-primary-600">
                    Per year
                  </div>
                  <div className="mt-1 text-2xl font-bold text-primary-700">
                    {usd(estimate.total * 12)}
                  </div>
                </div>
              </div>
              <p className="mt-2 text-right text-xs text-gray-400">
                Yearly = this month&apos;s activity × 12
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
}
